import { AuthProvider } from "./common";
import { EmailDeliveryChain } from "./postman.types";

/* eslint-disable @typescript-eslint/no-explicit-any */
export enum PlacementStatus {
  PENDING = "pending",
  INBOX = "inbox",
  SPAM = "spam",
  UNDELIVERED = "undelivered",
  UNKNOWN = "unknown",
}

export enum EmailProvider {
  GMAIL = "gmail",
  OUTLOOK = "outlook",
  YAHOO = "yahoo",
  AOL = "aol",
  ICLOUD = "icloud",
  GMX = "gmx",
  PROTONMAIL = "protonmail",
  MICROSOFT365 = "microsoft365",
  GOOGLEWORKSPACE = "googleworkspace",
  ZOHO = "zoho",
  AMAZONSES = "amazon ses",
  AMAZONSPAMHAUS = "amazon spamhaus",
  AMAZONTRENDMICRO = "amazon trendmicro",
  AMAZONABUSEX = "amazon abusix",
  WORKMAIL = "workmail",
  OTHER = "other",
  RSPAMD = "rspamd",
  AZURE = "azure",
}

// DONT CHANGE THE ORDER OF THESE PROVIDERS, AS THIS IS USED FOR SORTING
export const b2bProviders: EmailProvider[] = [
  EmailProvider.GOOGLEWORKSPACE,
  EmailProvider.MICROSOFT365,
  EmailProvider.ZOHO,
  EmailProvider.WORKMAIL,
  // EmailProvider.AMAZONSES,
  EmailProvider.AMAZONABUSEX,
  EmailProvider.AMAZONSPAMHAUS,
  EmailProvider.AMAZONTRENDMICRO,
  EmailProvider.RSPAMD,
];

// DONT CHANGE THE ORDER OF THESE PROVIDERS, AS THIS IS USED FOR SORTING
export const b2cProviders: EmailProvider[] = [
  EmailProvider.GMAIL,
  EmailProvider.OUTLOOK,
  EmailProvider.YAHOO,
  EmailProvider.ICLOUD,
  EmailProvider.AOL,
  EmailProvider.GMX,
  // EmailProvider.PROTONMAIL,
];

export interface ProviderGridResult {
  status: PlacementStatus;
  deliveryRate: number;
  category?: string | null;
}

export interface PlacementGridItem {
  email: string;
  type: PlacementType;
  score: number;
  testId: string;
  createdAt: string;
  isCompleted: boolean;
  sentAt: string;
  updatedAt: string;

  // B2B providers
  microsoft365?: ProviderGridResult;
  googleWorkspace?: ProviderGridResult;
  zoho?: ProviderGridResult;

  // B2C providers
  gmail?: ProviderGridResult;
  outlook?: ProviderGridResult;
  yahoo?: ProviderGridResult;
  aol?: ProviderGridResult;
  icloud?: ProviderGridResult;
  gmx?: ProviderGridResult;
  protonmail?: ProviderGridResult;
}

export interface PlacementGridResponse {
  tests: PlacementGridItem[];
  total: number;
  page: number;
  pages: number;
}

export enum PlacementType {
  B2B = "B2B",
  B2C = "B2C",
}

// New type for the full API response
export interface InboxPlacementApiResponse {
  success: boolean;
  summary: PlacementGridResponse;
}
export interface PlacementDataResponse {
  averageScore: number;
  inboxPlacement: number;
  spamPlacement: number;
  undelivered: number;
  totalTests: number;
  completedTests: number;
  providerStats: Record<
    string,
    {
      total: number;
      inbox: number;
      spam: number;
      undelivered: number;
      inboxRate: number;
    }
  >;
  // Add provider-specific data based on type
  providerBreakdown: {
    name: string;
    inboxRate: number;
    spamRate: number;
    undeliveredRate: number;
  }[];
}

export interface ProviderResult {
  provider: string;
  displayName: string;
  inbox: number;
  spam: number;
  unreceived: number;
  testType: PlacementType;
}

export interface TestResultSummary {
  inbox: {
    count: number;
    percentage: number;
  };
  spam: {
    count: number;
    percentage: number;
  };
  unreceived: {
    count: number;
    percentage: number;
  };
  total: number;
}

export interface TestInfo {
  id: string;
  senderEmail: string;
  subject: string;
  sentAt: Date;
  completedAt?: Date;
  isCompleted: boolean;
  testType: string;
  htmlBody: string;
  textBody: string;
  isPublic: boolean;
  publishedAt?: string;
  companyId: string;
}

export interface DetailedProviderData {
  providerDetails: Record<
    string,
    {
      receiverResults: Record<string, any>;
      overallStatus: string;
      deliveryRate: number;
      averageDeliveryTime: number;
    }
  >;
}

export interface FormattedTestResultsResponse {
  success: boolean;
  message?: string;
  test?: TestInfo;
  summary?: TestResultSummary;
  providerResults?: ProviderResult[];
}

export interface EmailAuthStatus {
  dkim: boolean;
  spf: boolean;
  dmarc: boolean;
}

export interface EmailDeliveryStatus {
  email: string;
  deliveredTo: string; // INBOX, SPAM, UNRECEIVED
  category?: string; // PRIMARY, PROMOTION, etc.
  senderIP: string;
  authStatus: EmailAuthStatus;
}

export interface ProviderDetailedResult {
  provider: string;
  displayName: string;
  emailCount: number;
  inboxPercentage: number;
  spamPercentage: number;
  unreceivedPercentage: number;
  authStatus: {
    dkim: boolean;
    spf: boolean;
    dmarc: boolean;
  };
  categoryBreakdown?: {
    primary?: {
      count: number;
      percentage: number;
    };
    promotion?: {
      count: number;
      percentage: number;
    };
    updates?: {
      count: number;
      percentage: number;
    };
    other?: {
      count: number;
      percentage: number;
    };
  };
  emails: EmailDeliveryStatus[];
}

export interface DetailedProviderBreakdownResponse {
  success: boolean;
  message?: string;
  test?: {
    id: string;
    senderEmail: string;
    subject: string;
    sentAt: Date;
    completedAt?: Date;
    isCompleted: boolean;
    testType: string;
  };
  providers: ProviderDetailedResult[];
}

export interface BlacklistCheck {
  uriChecked: string;
  ipBlacklistsResults: Array<{
    rblName: string;
    listed: boolean;
    response: string;
    aLookuptTimeout: boolean;
  }>;
  domainBlacklistsResults: Array<{
    rblName: string;
    listed: boolean;
    description: string;
    response: string;
    aLookuptTimeout: boolean;
  }>;
}

export interface EmailDetailResponse {
  // Basic email information
  to: string;
  from: {
    value: {
      address: string;
      name: string;
    }[];
    html: string;
    text: string;
  };
  senderIP: string;
  subject: string;
  date: Date;
  messageId: string;
  formattedDeliveryChain: EmailDeliveryChain[];
  relaycheck: {
    openRelay: boolean;
    startTlsSupported: boolean;
    sessionLines: string[];
    errored: boolean;
  };
  // Delivery information
  deliveryStatus: string; // INBOX, SPAM, UNRECEIVED
  category?: string; // PRIMARY, PROMOTION, etc.
  deliveryChain: string[];
  rawContent: string;
  // Authentication results
  authResults: {
    spf: string;
    dkim: string;
    dmarc: string;
  };

  // Content
  plainText?: string;
  htmlContent?: string;

  // Headers and technical details
  headers: Record<string, string | string[]>;

  // Blacklist information
  blacklistReport?: {
    senderIP?: {
      checks: BlacklistCheck[];
    };
    messageIP?: {
      checks: BlacklistCheck[];
    };
    domain?: {
      checks: BlacklistCheck[];
    };
    mxServer?: {
      checks: BlacklistCheck[];
    };
  };

  domainBlacklist?: {
    server: string;
    blacklists: {
      rblName: string;
      listed: boolean;
      description: string;
      response: string;
      aLookuptTimeout: boolean;
    }[];
  }[];

  mxServerBlacklists: {
    server: string;
    blacklists: {
      rblName: string;
      listed: boolean;
      description: string;
      response: string;
      aLookuptTimeout: boolean;
    }[];
  }[];
  smtpBlacklist: {
    server: string;
    blacklists: {
      rblName: string;
      listed: boolean;
      response: string;
      aLookuptTimeout: boolean;
    }[];
  }[];
  // Problems detected
  problems: Array<{
    type: string;
    description: string;
    severity: "high" | "medium" | "low";
  }>;
}

export interface TestHistoryItem {
  id: string;
  date: Date;
  type: string; // B2B or B2C
  score: number;
  isCompleted: boolean;
  isPublic: boolean;
  publishedAt: Date;
  companyId: string;

  // Provider results
  providerResults: {
    [provider: string]: {
      status: string; // INBOX, SPAM, UNRECEIVED
      deliveryRate: number;
      category?: string; // For Gmail: PRIMARY, PROMOTIONS, etc.
    };
  };
}

export interface TestHistoryResponse {
  success: boolean;
  message?: string;
  senderEmail: string;
  tests: TestHistoryItem[];
  total: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface BulkPlacementReportStatusItem {
  testId: string;
  status: boolean;
  message: "FOUND" | "NOT_FOUND";
}

export type RecurrenceType =
  | "doesnt-repeat"
  | "daily"
  | "weekly"
  | "monthly-by-day"
  | "monthly-by-date"
  | "annually"
  | "weekdays"
  | "custom";

export interface CustomRecurrence {
  interval: number;
  unit: "day" | "week" | "month" | "year";
  endType: "never" | "on" | "after";
  endDate?: Date;
  occurrences?: number;
  weekdays?: number[];
}

export interface ISchedule {
  type: "once" | "recurring";
  recurrenceType: RecurrenceType;
  startTime: string;
  customRecurrence?: CustomRecurrence;
}

export interface IScheduledPlacementTest {
  _id: string;
  mailbox: string[];
  template: {
    _id: string;
    title: string;
  };
  schedule: ISchedule;
  provider: string[];
  companyId: string; // ObjectId stored as string in TypeScript
  lastRun: string;
  nextRun: string;
  active: boolean;
  status: "pending" | "active" | "completed" | "failed";
  type: "B2B" | "B2C";
  espType: AuthProvider;
  createdAt: string;
  updatedAt: string;
}

export interface IScheduledEmailHealthTest {
  _id: string;
  domains: string[];
  schedule: ISchedule;
  companyId: string; // ObjectId stored as string in TypeScript
  lastRun: string;
  nextRun: string;
  espType: AuthProvider;
  active: boolean;
  status: "pending" | "active" | "completed" | "failed";
  type: "placement" | "email-health";
  createdAt: string;
  updatedAt: string;
}
